import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Loader2, RefreshCw, Wrench, Globe2, ChevronDown, ChevronRight, Trophy, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function CannibalizationDashboard() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [siteData, setSiteData] = useState(null);
  const [bulkScan, setBulkScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState({});

  useEffect(() => {
    document.title = "Cannibalization | Epidemos Admin";
    axios.get(`${BACKEND_URL}/api/sites?status=active`)
      .then(r => setSites(Array.isArray(r.data) ? r.data : r.data.sites || []))
      .catch(() => {});
  }, []);

  const scanSite = async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/admin/cannibalization/site/${siteId}`);
      setSiteData(r.data);
    } finally { setLoading(false); }
  };

  const fixSite = async () => {
    if (!siteId || !window.confirm(`Naprawdę naprawić wszystkich loserów dla tego portala? Loserzy dostaną <meta noindex>.`)) return;
    setFixing(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/cannibalization/fix/${siteId}?max_fixes=50`);
      alert(`Fixed: ${r.data.fixed}, Failed: ${r.data.failed || 0}`);
      scanSite();
    } finally { setFixing(false); }
  };

  const scanAll = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/admin/cannibalization/scan-all?limit_sites=50`);
      setBulkScan(r.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (siteId) scanSite(); }, [siteId]);

  return (
    <div className="p-6 max-w-none mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-600" /> Cannibalization Detector
          </h1>
          <p className="text-sm text-slate-500">Identyfikacja postów które rywalizują o ten sam keyword. Loserzy dostają &lt;meta noindex&gt; — equity przepływa do winnera.</p>
        </div>
        <button onClick={scanAll} disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Skanuj wszystkie portale
        </button>
      </div>

      {/* Bulk scan summary */}
      {bulkScan && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
          <h2 className="font-bold mb-2 text-amber-900">Wynik bulk skanu</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><strong>{bulkScan.sites_scanned}</strong> portali zeskanowano</div>
            <div><strong className="text-red-600 text-xl">{bulkScan.total_cannibalization_groups}</strong> grup konfliktów</div>
            <div><strong className="text-red-600 text-xl">{bulkScan.total_loser_posts}</strong> loser posts</div>
          </div>
          <div className="mt-4 text-xs">
            <div className="font-semibold mb-1">TOP 10 portali z najwięcej cannibalization:</div>
            <table className="w-full text-xs">
              <thead className="bg-amber-100">
                <tr><th className="text-left p-1">Portal</th><th className="text-right p-1">Grupy</th><th className="text-right p-1">Losers</th><th className="p-1"></th></tr>
              </thead>
              <tbody>
                {(bulkScan.per_site || []).filter(s => s.losers > 0).slice(0, 10).map(s => (
                  <tr key={s.site_id} className="border-t border-amber-200">
                    <td className="p-1">{s.name}</td>
                    <td className="p-1 text-right">{s.groups}</td>
                    <td className="p-1 text-right font-bold text-red-700">{s.losers}</td>
                    <td className="p-1"><button onClick={() => { setSiteId(s.site_id); window.scrollTo(0,0); }} className="text-blue-600 hover:underline">→</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Site selector */}
      <div className="mb-6 flex gap-3 items-end">
        <div className="flex-1">
          <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Portal</label>
          <select value={siteId} onChange={e => setSiteId(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg">
            <option value="">— wybierz portal —</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name || s.url}</option>)}
          </select>
        </div>
        {siteData?.cannibalization_groups > 0 && (
          <button onClick={fixSite} disabled={fixing}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50">
            {fixing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
            Auto-fix wszystkich loserów
          </button>
        )}
      </div>

      {siteId && siteData && (
        <>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-slate-50 rounded">
              <div className="text-xs text-slate-500">Posts skanowane</div>
              <div className="text-xl font-bold">{siteData.scanned_posts}</div>
            </div>
            <div className="p-3 bg-blue-50 rounded">
              <div className="text-xs text-blue-700">Unikalne keywordy</div>
              <div className="text-xl font-bold text-blue-700">{siteData.unique_keywords}</div>
            </div>
            <div className="p-3 bg-amber-50 rounded">
              <div className="text-xs text-amber-700">Grupy konfliktów</div>
              <div className="text-xl font-bold text-amber-700">{siteData.cannibalization_groups}</div>
            </div>
            <div className="p-3 bg-red-50 rounded">
              <div className="text-xs text-red-700">Loser posts</div>
              <div className="text-xl font-bold text-red-700">{siteData.loser_posts_count}</div>
            </div>
          </div>

          {(siteData.issues || []).length === 0 ? (
            <div className="p-6 bg-green-50 border border-green-200 rounded text-green-900 text-center">
              ✓ Brak cannibalizacji — każdy keyword ma 1 winner
            </div>
          ) : (
            <div className="space-y-2">
              {siteData.issues.map((iss, i) => {
                const isExp = expandedGroup[i];
                return (
                  <div key={i} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                    <button onClick={() => setExpandedGroup({ ...expandedGroup, [i]: !isExp })}
                      className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50">
                      {isExp ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium">{iss.keyword_sample}</div>
                        <div className="text-xs text-slate-500">{iss.count} posty rywalizują</div>
                      </div>
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">{iss.count}</span>
                    </button>
                    {isExp && (
                      <div className="border-t border-slate-100 p-3 space-y-2 bg-slate-50">
                        <div className="flex items-start gap-2 text-sm">
                          <Trophy className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-amber-900">WINNER (score: {iss.winner.score || "?"})</div>
                            <div className="text-xs text-slate-700">{iss.winner.title}</div>
                            <a href={iss.winner.url} target="_blank" rel="noopener" className="text-xs text-blue-600 hover:underline">{iss.winner.url}</a>
                          </div>
                        </div>
                        {iss.losers.map((L, j) => (
                          <div key={j} className="flex items-start gap-2 text-sm pl-4">
                            <X className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <div className="font-medium text-red-700">LOSER (score: {L.score || "?"}) — dostanie noindex</div>
                              <div className="text-xs text-slate-700">{L.title}</div>
                              <a href={L.url} target="_blank" rel="noopener" className="text-xs text-blue-600 hover:underline">{L.url}</a>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
